import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Upload, Trash2, Loader2, Building2, Lock } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { Link } from "wouter";
import type { User } from "@shared/schema";

export default function EditProfile() {
  const { user: authUser } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: currentUser, isLoading } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  const [formData, setFormData] = useState({
    hasInsurance: false,
    insuranceProvider: "",
  });
  const [formInit, setFormInit] = useState(false);

  useEffect(() => {
    if (currentUser && !formInit) {
      setFormData({
        hasInsurance: currentUser.hasInsurance || false,
        insuranceProvider: currentUser.insuranceProvider || "",
      });
      setFormInit(true);
    }
  }, [currentUser, formInit]);

  const { uploadFile, isUploading } = useUpload({
    allowedTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    onSuccess: (response) => {
      logoMutation.mutate({ companyLogo: response.objectPath });
    },
    onError: (error) => {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload logo. Please try a different image file.",
        variant: "destructive",
      });
    },
  });

  const logoMutation = useMutation({
    mutationFn: async (data: { companyLogo: string | null }) =>
      apiRequest("PATCH", "/api/profile", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      if (authUser?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/users/${authUser.id}/profile`] });
      }
      toast({ title: "Logo updated", description: "Your company logo has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update logo.", variant: "destructive" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", "/api/profile", {
        hasInsurance: formData.hasInsurance,
        insuranceProvider: formData.insuranceProvider,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      if (authUser?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/users/${authUser.id}/profile`] });
      }
      toast({ title: "Profile updated", description: "Your company profile has been saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to save profile.", variant: "destructive" });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please select an image file (PNG, JPG, etc.)", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Please select an image under 5MB", variant: "destructive" });
      return;
    }
    await uploadFile(file);
  };

  const getInitials = () => {
    if (!currentUser) return "??";
    if (currentUser.company) return currentUser.company.substring(0, 2).toUpperCase();
    const first = currentUser.firstName?.[0] || "";
    const last = currentUser.lastName?.[0] || "";
    return (first + last).toUpperCase() || "U";
  };

  const showInsurance =
    currentUser?.userType === "Carrier" || currentUser?.userType === "Broker";

  if (isLoading) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Edit Company Profile</h1>
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <Link href={authUser?.id ? `/profile/${authUser.id}` : "/dashboard"}>
          <Button variant="ghost" className="mb-3 -ml-2" data-testid="link-back-to-profile">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
        </Link>
        <h1 className="text-3xl font-bold">Edit Company Profile</h1>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Company Logo</h2>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <Avatar className="w-20 h-20 border-2 border-border" data-testid="avatar-company-logo">
            {currentUser?.companyLogo ? (
              <AvatarImage
                src={
                  currentUser.companyLogo.startsWith("/objects/")
                    ? currentUser.companyLogo
                    : `/objects/${currentUser.companyLogo}`
                }
                alt="Company Logo"
                className="object-cover"
              />
            ) : null}
            <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
          </Avatar>

          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="input-logo-file"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || logoMutation.isPending}
              data-testid="button-upload-logo"
            >
              {isUploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />{currentUser?.companyLogo ? "Change Logo" : "Upload Logo"}</>
              )}
            </Button>
            {currentUser?.companyLogo && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoMutation.mutate({ companyLogo: null })}
                disabled={logoMutation.isPending}
                data-testid="button-remove-logo"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Logo
              </Button>
            )}
            <p className="text-xs text-muted-foreground">PNG, JPG, or GIF up to 5MB.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Company Information</h2>
        </div>

        <div className="space-y-4">
          {/* Locked: Company Name */}
          <div className="space-y-2">
            <Label>Company Name</Label>
            <div className="relative">
              <Input
                value={currentUser?.company ?? ""}
                readOnly
                className="bg-muted pr-9"
                data-testid="input-company"
              />
              <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">Cannot be changed. Contact support if this needs to be updated.</p>
          </div>

          {/* Locked: DOT Number (if present) */}
          {currentUser?.dotNumber && (
            <div className="space-y-2">
              <Label>DOT Number</Label>
              <div className="relative">
                <Input
                  value={currentUser.dotNumber}
                  readOnly
                  className="bg-muted pr-9"
                  data-testid="input-dot-number"
                />
                <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">Cannot be changed. Contact support if this needs to be updated.</p>
            </div>
          )}

          {/* Locked: MC Number (if present) */}
          {currentUser?.mcNumber && (
            <div className="space-y-2">
              <Label>MC Number</Label>
              <div className="relative">
                <Input
                  value={currentUser.mcNumber}
                  readOnly
                  className="bg-muted pr-9"
                  data-testid="input-mc-number"
                />
                <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">Cannot be changed. Contact support if this needs to be updated.</p>
            </div>
          )}

          {/* Locked: Email */}
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Input
                value={currentUser?.email ?? ""}
                readOnly
                className="bg-muted pr-9"
                data-testid="input-email-locked"
              />
              <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">Cannot be changed. Contact support if this needs to be updated.</p>
          </div>

          {showInsurance && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="font-medium text-sm">Insurance Information</p>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasInsurance"
                    checked={formData.hasInsurance}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, hasInsurance: checked === true })
                    }
                    data-testid="checkbox-has-insurance"
                  />
                  <Label htmlFor="hasInsurance" className="cursor-pointer">
                    I have active cargo/liability insurance
                  </Label>
                </div>
                {formData.hasInsurance && (
                  <div className="space-y-2 pl-6">
                    <Label htmlFor="insuranceProvider">Insurance Provider (Optional)</Label>
                    <Input
                      id="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={(e) =>
                        setFormData({ ...formData, insuranceProvider: e.target.value })
                      }
                      placeholder="e.g., Progressive, Great West Casualty"
                      data-testid="input-insurance-provider"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="button-save-profile"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
